import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { API_BASE_URL } from '../config';
import SavedSearchPreview from './SavedSearchPreview';

export default function SaveSearchModal({ isOpen, onClose, currentFilters, fetchWithAuth }) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const i18n = useMemo(() => ({
    client: t('Client'),
    shopcity: t('City'),
    username: t('Team'),
    shopcountry: t('Country'),
    sector: t('Sector'),
    shopname: t('Shop name'),
    shopprovince: t('Province'),
    shopsign: t('Shop sign'),
    project: t('Program'),
    shopzipcode: t('Zip code'),
    item: t('Item'),
    translatedquestion: t('Question'),
    createdat: t('Period'),
  }), [t]);

  const getFilterSummary = () => {
    return Object.entries(currentFilters)
      .filter(([key, value]) => {
        // Ignore empty values and createdat
        if (!value || key === 'createdat') return false;
        return true;
      })
      .map(([key, value]) => {
        // Handle different value types
        let displayValue = '';
        if (Array.isArray(value)) {
          displayValue = value.map(v => v.label).join(', ');
        } else if (typeof value === 'object') {
          displayValue = value.label;
        } else {
          displayValue = value.toString();
        }

        return {
          label: i18n[key] || key,
          value: displayValue
        };
      });
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/saved-searches`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name,
          filters: currentFilters
        }),
      });

      const savedSearch = await response.json();

      // Appliquer la recherche sauvegardée immédiatement
      onClose(savedSearch); // Modifier le onClose pour accepter la recherche sauvegardée

    } catch (error) {
      console.error('Failed to save search:', error);
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  const filterSummary = getFilterSummary();

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Backdrop */}
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="inline-block px-4 pt-5 pb-4 overflow-hidden text-left align-bottom transition-all transform bg-white rounded-lg shadow-xl dark:bg-gray-800 sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
          <div className="sm:flex sm:items-start">
            <div className="w-full mt-3 text-center sm:mt-0 sm:text-left">
              <h3 className="text-lg font-medium leading-6 text-gray-900 dark:text-gray-100">
                {t('Save current search')}
              </h3>

              <div className="mt-4">
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t('Enter a name for this search')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                />
              </div>

              <div className="mt-4">
                <SavedSearchPreview filters={currentFilters} />
              </div>
            </div>
          </div>

          <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              onClick={handleSave}
              disabled={!name.trim() || isSaving}
              className="inline-flex justify-center w-full px-4 py-2 text-base font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? t('Saving...') : t('Save')}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="mt-3 inline-flex justify-center w-full px-4 py-2 text-base font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:w-auto sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600"
            >
              {t('Cancel')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}